import type { PartnerServiceListItem } from "@/types/partner-settings";

export type ServicesSortDirection = "asc" | "desc";

export type ServicesSortState = {
  field: string | null;
  direction: ServicesSortDirection | null;
};

export const DEFAULT_SERVICES_SORT: ServicesSortState = {
  field: null,
  direction: null,
};

/** Header click cycle: new field -> asc, same field asc -> desc, desc -> no sorting. */
export function getNextServicesSort(
  current: ServicesSortState,
  field: string,
): ServicesSortState {
  if (current.field !== field) {
    return { field, direction: "asc" };
  }
  if (current.direction === "asc") {
    return { field, direction: "desc" };
  }
  if (current.direction === "desc") {
    return DEFAULT_SERVICES_SORT;
  }
  return { field, direction: "asc" };
}

/** Any change of sort field or direction returns the paginator to page 1. */
export function getPageAfterSortChange(
  previous: ServicesSortState,
  next: ServicesSortState,
  currentPage: number,
): number {
  return previous.field === next.field && previous.direction === next.direction
    ? currentPage
    : 1;
}

/** Sorts the rows of the loaded page (the services endpoint has no sort parameter). */
export function sortServices(
  services: PartnerServiceListItem[],
  sort: ServicesSortState,
): PartnerServiceListItem[] {
  const { field, direction } = sort;
  if (!field || !direction) {
    return services;
  }

  return [...services].sort((a, b) => {
    let aVal: unknown;
    let bVal: unknown;

    if (field === "categories") {
      aVal = a.categories[0]?.name ?? "";
      bVal = b.categories[0]?.name ?? "";
    } else {
      aVal = (a as unknown as Record<string, unknown>)[field];
      bVal = (b as unknown as Record<string, unknown>)[field];
    }

    if (aVal === null || aVal === undefined) aVal = "";
    if (bVal === null || bVal === undefined) bVal = "";

    let comparison = 0;
    if (typeof aVal === "string" && typeof bVal === "string") {
      comparison = aVal.localeCompare(bVal);
    } else if (typeof aVal === "number" && typeof bVal === "number") {
      comparison = aVal - bVal;
    } else if (typeof aVal === "boolean" && typeof bVal === "boolean") {
      comparison = Number(aVal) - Number(bVal);
    }

    return direction === "asc" ? comparison : -comparison;
  });
}
